import { useState, useEffect, useRef } from 'react';

export default function AboutSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 px-6 bg-[#0d1117]">
      <div className="max-w-6xl mx-auto">
        <div
          className={`transition-all duration-1000 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
          }`}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">
            <span className="bg-gradient-to-r from-[#3ECF8E] to-[#2dd4bf] bg-clip-text text-transparent">
              About Me
            </span>
          </h2>

          <div className="grid md:grid-cols-2 gap-8 items-start">
            {/* Left Content */}
            <div className="space-y-6">
              {/* 서버 · 백엔드 소개 */}
              <div className="relative bg-gradient-to-br from-[#161b22] to-[#0d1117] border border-[#30363d] rounded-2xl p-8 hover:border-[#3ECF8E]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#3ECF8E]/10">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#3ECF8E]/5 rounded-full blur-3xl" />
                <div className="relative">
                  <h3 className="text-2xl font-bold mb-6 text-[#3ECF8E]">
                    서버·백엔드 개발
                  </h3>
                  <div className="text-gray-300 leading-relaxed space-y-4">
                    <div className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#3ECF8E] mt-1 flex-shrink-0 text-lg" />
                      <p>
                        사용자 수가 많은 환경에서{' '}<strong className="text-white">안정성과 일관성</strong>을 우선으로 개발합니다. 신규 기능을 빠르게 만드는 것보다,{' '}<strong className="text-white">운영 가능한 구조로 설계하고 일관성 있게 개선</strong>하는 것을 지향합니다.
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#3ECF8E] mt-1 flex-shrink-0 text-lg" />
                      <p>
                        왜 이렇게 설계했는지 설명할 수 있는 코드, 다른 동료가 이어서 유지보수할 수 있는 구조를 목표로 개발합니다.
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#3ECF8E] mt-1 flex-shrink-0 text-lg" />
                      <p>
                        메시지 발송·결제·웹서비스 등 다양한 환경에서 실제 장애와
                        문제를 해결한 경험이 있습니다.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 핵심 경험 */}
              <div className="relative bg-gradient-to-br from-[#161b22] to-[#0d1117] border border-[#30363d] rounded-2xl p-8 hover:border-[#3ECF8E]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#3ECF8E]/10">
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-[#2dd4bf]/5 rounded-full blur-3xl" />
                <div className="relative">
                  <h3 className="text-xl font-bold mb-6 text-white">핵심 경험</h3>
                  <div className="grid gap-4">
                    <div className="p-4 bg-[#0d1117]/50 rounded-lg border border-[#30363d]/50">
                      <strong className="text-white block mb-1">장애 대응 & API 개발</strong>
                      <span className="text-gray-400 text-sm">
                        스펙 변경 대응 및 신규 API·배치(Job) 개발
                      </span>
                    </div>
                    <div className="p-4 bg-[#0d1117]/50 rounded-lg border border-[#30363d]/50">
                      <strong className="text-white block mb-1">웹서비스 전 과정</strong>
                      <span className="text-gray-400 text-sm">
                        기획·개발·테스트·배포·운영까지 참여
                      </span>
                    </div>
                    <div className="p-4 bg-[#0d1117]/50 rounded-lg border border-[#30363d]/50">
                      <strong className="text-white block mb-1">Spring, Java, Go</strong>
                      <span className="text-gray-400 text-sm">
                        서버·배치 개발 및 구조 개선
                      </span>
                    </div>
                    <div className="p-4 bg-[#0d1117]/50 rounded-lg border border-[#30363d]/50">
                      <strong className="text-white block mb-1">배포 자동화 & 운영</strong>
                      <span className="text-gray-400 text-sm">
                        Jenkins, AWS CodeDeploy, Nginx 활용
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Content */}
            <div className="space-y-6">
              {/* 리더십 경험 */}
              <div className="relative bg-gradient-to-br from-[#161b22] to-[#0d1117] border border-[#30363d] rounded-2xl p-8 hover:border-[#3ECF8E]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#3ECF8E]/10">
                <div className="absolute top-0 left-0 w-32 h-32 bg-[#3ECF8E]/5 rounded-full blur-3xl" />
                <div className="relative">
                  <h3 className="text-xl font-bold mb-6 text-white">리더십 경험</h3>
                  <div className="space-y-4">
                    <div className="p-4 bg-[#0d1117]/50 rounded-lg border border-[#30363d]/50">
                      <strong className="text-[#3ECF8E] block mb-2">공과대학 학생회장</strong>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        <strong className="text-white">40명 규모 조직</strong>을 1년간 이끌며,
                        이해관계가 다른 사람들의 의견을 조율하고 실행으로 옮기는 경험을
                        쌓았습니다.
                      </p>
                    </div>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      이때 배운 소통 방식과 책임감은 지금도 팀과 함께 개발할 때 제가 가장
                      중요하게 생각하는 기준입니다.
                    </p>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="space-y-4">
                <div className="bg-gradient-to-br from-[#161b22] to-[#0d1117] border border-[#30363d] rounded-xl p-6 text-center hover:border-[#3ECF8E]/50 transition-all duration-300">
                  <div className="text-3xl font-bold text-[#3ECF8E] mb-2">
                    5배+
                  </div>
                  <div className="text-sm text-gray-400 whitespace-nowrap">
                    Go 전환으로 데이터 동기화 프로세스 성능 개선
                  </div>
                </div>

                <div className="bg-gradient-to-br from-[#161b22] to-[#0d1117] border border-[#30363d] rounded-xl p-6 text-center hover:border-[#3ECF8E]/50 transition-all duration-300">
                  <div className="text-3xl font-bold text-[#3ECF8E] mb-2">
                    10개+
                  </div>
                  <div className="text-sm text-gray-400 whitespace-nowrap">
                    팀·실무 프로젝트에서 경험한 서비스 개발
                  </div>
                </div>

                <div className="bg-gradient-to-br from-[#161b22] to-[#0d1117] border border-[#30363d] rounded-xl p-6 text-center hover:border-[#3ECF8E]/50 transition-all duration-300">
                  <div className="text-3xl font-bold text-[#3ECF8E] mb-2">
                    100만 건/일
                  </div>
                  <div className="text-sm text-gray-400 whitespace-nowrap">
                    운영 환경에서 다루는 API 서비스 일일 평균 요청 규모
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
